// AdminFieldConfig — admin-only page where the team can toggle each
// School-Setup field as mandatory or optional. Saves to
// PUT /api/admin/field-config, refreshes the shared cache so any open
// Setup.jsx tab picks up the new asterisk state on next render.
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import {
  Save, RotateCcw, ShieldCheck, Loader2, ChevronDown, ChevronUp, AlertCircle,
} from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { invalidateFieldConfig } from "@/lib/useFieldConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_META = {
  schoolIdentity: { label: "School Identity",  icon: "🏫", hint: "All address & UDISE fields shown on the Edit School form." },
  vendor:         { label: "Vendor Details",   icon: "🛒", hint: "Fields shown on each Vendor row inside the Vendors tab." },
  cch:            { label: "CCH (Cook) Details","icon": "👨‍🍳", hint: "Fields shown on each Cook-cum-Helper row inside the CCH tab." },
};

export default function AdminFieldConfig() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [config, setConfig] = useState({ sections: {}, required: {} });
  const [openSections, setOpenSections] = useState({ schoolIdentity: true, vendor: true, cch: true });

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/admin/field-config`);
      setConfig(resp.data || { sections: {}, required: {} });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load config");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchConfig(); }, []);

  const toggle = (section, fieldKey) => {
    setConfig((prev) => {
      const sectionMap = { ...(prev.required?.[section] || {}) };
      sectionMap[fieldKey] = !sectionMap[fieldKey];
      return { ...prev, required: { ...prev.required, [section]: sectionMap } };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const resp = await axios.put(`${API}/admin/field-config`, { required: config.required });
      setConfig(resp.data);
      invalidateFieldConfig();
      toast.success("Field config saved. Setup forms will reflect this immediately.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save config");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset every field to its default mandatory state?")) return;
    setResetting(true);
    try {
      const resp = await axios.post(`${API}/admin/field-config/reset`);
      setConfig(resp.data);
      invalidateFieldConfig();
      toast.success("Reset to defaults.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset config");
    } finally {
      setResetting(false);
    }
  };

  if (user?.role !== "admin") {
    return (
      <main className="min-h-screen bg-sand-50 text-ink-900">
        <Navbar />
        <div className="mx-auto max-w-3xl px-4 py-20 text-center">
          <AlertCircle className="mx-auto h-10 w-10 text-rose-600" />
          <h2 className="mt-4 text-xl font-semibold text-rose-900">Admin access required</h2>
          <p className="mt-2 text-sm text-rose-800">This page is only accessible to administrators.</p>
        </div>
      </main>
    );
  }

  const sectionEntries = Object.entries(config.sections || {});
  const totalMandatory = sectionEntries.reduce((acc, [sectionKey, fields]) => {
    const r = config.required?.[sectionKey] || {};
    return acc + fields.filter((f) => r[f.key]).length;
  }, 0);
  const totalFields = sectionEntries.reduce((acc, [, fields]) => acc + fields.length, 0);

  return (
    <main className="min-h-screen bg-sand-50 text-ink-900">
      <Navbar />
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <p className="text-[0.7rem] font-semibold uppercase tracking-[0.28em] text-clay">Admin · Setup configuration</p>
          <h1 className="mt-2 font-heading text-3xl font-semibold tracking-tight text-ink-900 sm:text-4xl">
            Mandatory-field settings
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-ink-700">
            Toggle whether each field of the School Setup is mandatory for logged-in school users.
            When a field is marked <span className="font-semibold text-emerald-700">Mandatory</span>,
            it gets a red <span className="text-red-500">*</span> on the form and the school's setup
            is considered incomplete until that field is filled.
          </p>
          {loading ? (
            <p className="mt-3 inline-flex items-center gap-2 text-sm text-ink-700">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading current config…
            </p>
          ) : (
            <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800" data-testid="config-totals">
              <ShieldCheck className="h-3.5 w-3.5" />
              {totalMandatory} of {totalFields} fields marked mandatory
            </p>
          )}
        </div>

        {/* Sections */}
        {!loading && sectionEntries.map(([sectionKey, fields]) => {
          const meta = SECTION_META[sectionKey] || { label: sectionKey, icon: "📋", hint: "" };
          const required = config.required?.[sectionKey] || {};
          const mandatoryCount = fields.filter((f) => required[f.key]).length;
          const isOpen = openSections[sectionKey];
          return (
            <section
              key={sectionKey}
              className="mb-5 overflow-hidden rounded-2xl border border-border bg-white shadow-sm"
              data-testid={`section-${sectionKey}`}
            >
              <button
                type="button"
                onClick={() => setOpenSections((p) => ({ ...p, [sectionKey]: !p[sectionKey] }))}
                className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left transition hover:bg-sand-50"
                data-testid={`section-toggle-${sectionKey}`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{meta.icon}</span>
                  <div>
                    <h2 className="font-heading text-lg font-semibold text-ink-900">{meta.label}</h2>
                    <p className="mt-0.5 text-xs text-ink-700">{meta.hint}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-emerald-700">
                    {mandatoryCount}/{fields.length} mandatory
                  </span>
                  {isOpen ? <ChevronUp className="h-4 w-4 text-ink-700" /> : <ChevronDown className="h-4 w-4 text-ink-700" />}
                </div>
              </button>

              {isOpen && (
                <div className="border-t border-border px-5 py-4">
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {fields.map((f) => {
                      const on = !!required[f.key];
                      return (
                        <button
                          key={f.key}
                          type="button"
                          onClick={() => toggle(sectionKey, f.key)}
                          className={`flex items-center justify-between gap-3 rounded-lg border-2 px-3 py-2.5 text-left transition ${on ? "border-emerald-300 bg-emerald-50" : "border-gray-200 bg-gray-50 hover:bg-gray-100"}`}
                          data-testid={`toggle-${sectionKey}-${f.key}`}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-ink-900">
                              {f.label_en}{on && <span className="ml-1 text-red-500">*</span>}
                            </p>
                            <p className="truncate text-[0.7rem] text-ink-700">{f.label_od}</p>
                          </div>
                          <span
                            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition ${on ? "bg-emerald-500" : "bg-gray-300"}`}
                            aria-checked={on}
                            role="switch"
                          >
                            <span
                              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${on ? "translate-x-5" : "translate-x-0.5"}`}
                            />
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </section>
          );
        })}

        {/* Action bar */}
        {!loading && (
          <div className="sticky bottom-4 z-10 mt-8 flex flex-wrap items-center justify-between gap-3 rounded-2xl border-2 border-clay/30 bg-white p-4 shadow-lg">
            <div className="flex items-center gap-2 text-sm text-ink-700">
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
              <span>Changes apply to <b>every school</b> on the platform once saved.</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={reset}
                disabled={resetting || saving}
                data-testid="reset-defaults-btn"
              >
                {resetting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
                Reset to defaults
              </Button>
              <Button
                onClick={save}
                disabled={saving || resetting}
                className="bg-clay text-white hover:bg-clay-dark"
                data-testid="save-config-btn"
              >
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Save Changes
              </Button>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
